import { Component, inject, signal, effect, OnInit, OnDestroy } from '@angular/core';
import { AccountState, DeviceState, RouterState, MyStudentsState, MyGoalsState, MyObjectivesState, MyRemindersState, MyMeasurementsState, AdminStudentsState, AdminUsersState, AdminGoalsState, AdminMeasurementsState, AdminObservationsState, ActionBarState, SkillsState } from 'src/app/store';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { CalendarState } from 'src/app/layout/calendar';
import { DatePipe, JsonPipe, KeyValuePipe } from '@angular/common';
import { StateStatusIndicatorsComponent } from '../state-status-indicators/state-status-indicators.component';
import { Auth, onIdTokenChanged } from '@angular/fire/auth';
import { Unsubscribe } from '@angular/fire/firestore';

@Component({
  selector: 'app-state-inspector-panel',
  templateUrl: './state-inspector-panel.component.html',
  styleUrls: ['./state-inspector-panel.component.scss'],
  standalone: true,
  imports: [DatePipe, JsonPipe, KeyValuePipe, StateStatusIndicatorsComponent],
  host: {
    class:
      'block w-[20vw] min-w-[20vw] h-screen overflow-y-auto bg-surface pb-4 shadow-xl',
  },
})
export class StateInspectorPanelComponent implements OnInit, OnDestroy {
  private readonly auth = inject(Auth);
  private authStateUnsub?: Unsubscribe;

  // Inject states for inspection
  readonly device = inject(DeviceState);
  readonly actionBar = inject(ActionBarState);
  readonly router = inject(RouterState);
  readonly account = inject(AccountState);
  readonly calendar = inject(CalendarState);
  readonly myStudents = inject(MyStudentsState);
  readonly myGoals = inject(MyGoalsState);
  readonly myObjectives = inject(MyObjectivesState);
  readonly myReminders = inject(MyRemindersState);
  readonly myMeasurements = inject(MyMeasurementsState);
  readonly students = inject(AdminStudentsState);
  readonly users = inject(AdminUsersState);
  readonly adminGoals = inject(AdminGoalsState);
  readonly adminMeasurements = inject(AdminMeasurementsState);
  readonly adminObservations = inject(AdminObservationsState);
  readonly skills = inject(SkillsState);
  readonly profileState = inject(ProfileState);

  // Expansion state for each section
  readonly deviceExpanded = signal(false);
  readonly actionBarExpanded = signal(false);
  readonly routerExpanded = signal(false);
  readonly accountExpanded = signal(false);
  readonly calendarExpanded = signal(false);
  readonly myStudentsExpanded = signal(false);
  readonly myGoalsExpanded = signal(false);
  readonly myObjectivesExpanded = signal(false);
  readonly myRemindersExpanded = signal(false);
  readonly myMeasurementsExpanded = signal(false);
  readonly rostersExpanded = signal(false);
  readonly studentsExpanded = signal(false);
  readonly usersExpanded = signal(false);
  readonly adminGoalsExpanded = signal(false);
  readonly adminMeasurementsExpanded   = signal(false);
  readonly adminObservationsExpanded   = signal(false);
  readonly skillsExpanded = signal(false);

  /** Full ID token result from Firebase Auth. */
  readonly authToken = signal<Record<string, unknown> | null>(null);

  ngOnInit(): void {
    // onIdTokenChanged fires on sign-in, sign-out, AND every token refresh
    // (including force-refreshes triggered by setupTokenRefreshOnAccountUpdate).
    this.authStateUnsub = onIdTokenChanged(this.auth, user => {
      if (user) {
        this.refreshAuthToken();
      } else {
        this.authToken.set(null);
      }
    });

    // Also watch account/profile updatedAt directly so the Auth panel refreshes
    // even if Firebase doesn't issue a new token (e.g. claims unchanged server-side).
    let firstRun = true;
    effect(() => {
      this.account.data()?.updatedAt;
      this.account.activeProfile()?.updatedAt;

      if (firstRun) {
        firstRun = false;
        return;
      }

      // Brief delay to let AccountState's force-refresh complete before we read
      setTimeout(() => this.refreshAuthToken(), 1500);
    });
  }

  ngOnDestroy(): void {
    this.authStateUnsub?.();
  }

  async refreshAuthToken(): Promise<void> {
    try {
      const result = await this.auth.currentUser?.getIdTokenResult(true);
      this.authToken.set(result ? { ...result } as Record<string, unknown> : null);
    } catch {
      this.authToken.set(null);
    }
  }

  // Toggle methods
  toggleDeviceExpanded(): void {
    this.deviceExpanded.update(expanded => !expanded);
  }

  toggleActionBarExpanded(): void {
    this.actionBarExpanded.update(expanded => !expanded);
  }

  toggleRouterExpanded(): void {
    this.routerExpanded.update(expanded => !expanded);
  }

  toggleAccountExpanded(): void {
    this.accountExpanded.update(expanded => !expanded);
  }

  toggleMyStudentsExpanded(): void {
    this.myStudentsExpanded.update(expanded => !expanded);
  }

  toggleMyGoalsExpanded(): void {
    this.myGoalsExpanded.update(expanded => !expanded);
  }

  toggleMyObjectivesExpanded(): void {
    this.myObjectivesExpanded.update(expanded => !expanded);
  }

  toggleCalendarExpanded(): void {
    this.calendarExpanded.update(expanded => !expanded);
  }

  toggleMyRemindersExpanded(): void {
    this.myRemindersExpanded.update(expanded => !expanded);
  }

  toggleMyMeasurementsExpanded(): void {
    this.myMeasurementsExpanded.update(expanded => !expanded);
  }

  toggleRostersExpanded(): void {
    this.rostersExpanded.update(expanded => !expanded);
  }

  toggleStudentsExpanded(): void {
    this.studentsExpanded.update(expanded => !expanded);
  }

  toggleUsersExpanded(): void {
    this.usersExpanded.update(expanded => !expanded);
  }

  toggleAdminGoalsExpanded(): void {
    this.adminGoalsExpanded.update(expanded => !expanded);
  }

  toggleAdminMeasurementsExpanded(): void {
    this.adminMeasurementsExpanded.update(expanded => !expanded);
  }

  toggleAdminObservationsExpanded(): void {
    this.adminObservationsExpanded.update(expanded => !expanded);
  }

  toggleSkillsExpanded(): void {
    this.skillsExpanded.update(expanded => !expanded);
  }

  // =============================================
  // CACHE DIAGNOSTIC HELPERS
  // =============================================

  /**
   * Rough estimate of how many KB a cached payload for `count` records would be,
   * based on the average observed size per record for that state.
   * Returns a warning string if the estimate exceeds the 3.5 MB cap, otherwise null.
   */
  getCacheSizeWarning(stateName: 'students' | 'users' | 'goals' | 'measurements' | 'observations', count: number): string | null {
    // Average bytes per record (generous upper bound based on observed sizes)
    const AVG_BYTES: Record<string, number> = { students: 700, users: 900, goals: 2000, measurements: 500, observations: 600 };
    const limitBytes = 3.5 * 1024 * 1024;
    const estimated  = count * (AVG_BYTES[stateName] ?? 700);
    if (estimated > limitBytes) {
      return `~${(estimated / 1024 / 1024).toFixed(1)} MB — too large for localStorage`;
    }
    return null;
  }

  /** Returns the localStorage key used to cache the given state + team */
  getCacheKey(stateName: string, teamId: string | null): string {
    if (teamId) return `blume_state_cache_${stateName}_${teamId}`;
    return `blume_state_cache_${stateName}`;
  }

  /** Returns a human-readable age string from a lastSync timestamp (e.g. "3 min ago") */
  getCacheAgeMins(lastSync: number | null): string {
    if (!lastSync) return '—';
    const diffMs = Date.now() - lastSync;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return '< 1 min ago';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    return `${Math.floor(hours / 24)} day(s) ago`;
  }

  /**
   * Reads directly from localStorage and logs a full cache diagnostic report to
   * the console. Includes all blume_state_cache_* keys found AND an explicit check
   * of the expected keys for the currently active team.
   */
  dumpCacheKeysToConsole(): void {
    const prefix = 'blume_state_cache_';
    const found: Array<{ key: string; sizeKb: string; cachedAt: string; lastSync: string; count: number | string }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        try {
          const raw = localStorage.getItem(key)!;
          const parsed = JSON.parse(raw);
          found.push({
            key,
            sizeKb: `${(raw.length / 1024).toFixed(1)} KB`,
            cachedAt: parsed.cachedAt ? new Date(parsed.cachedAt).toLocaleString() : '—',
            lastSync: parsed.lastSync ? new Date(parsed.lastSync).toLocaleString() : '—',
            count: Array.isArray(parsed.data) ? parsed.data.length : '?',
          });
        } catch {
          found.push({ key, sizeKb: '?', cachedAt: 'parse error', lastSync: '—', count: '?' });
        }
      }
    }

    // ── All cache keys ────────────────────────────────────
    if (found.length === 0) {
      console.warn('[StateInspector] 🔴 No blume_state_cache_* keys found in localStorage!');
    } else {
      console.log(`[StateInspector] 📦 Found ${found.length} cache key(s) in localStorage:`);
      console.table(found);
    }

    // ── Explicit check for the expected students / users / goals keys ──
    const studentsTeamId  = this.students.activeTeamId();
    const usersTeamId     = this.users.activeTeamId();
    const goalsTeamId     = this.adminGoals.activeTeamId();

    const expectedStudentsKey = this.getCacheKey('students', studentsTeamId);
    const expectedUsersKey    = this.getCacheKey('users',    usersTeamId);
    const expectedGoalsKey        = this.getCacheKey('goals',        goalsTeamId);
    const measurementsTeamId       = this.adminMeasurements.activeTeamId();
    const expectedMeasurementsKey  = this.getCacheKey('measurements',  measurementsTeamId);
    const observationsTeamId       = this.adminObservations.activeTeamId();
    const expectedObservationsKey  = this.getCacheKey('observations',  observationsTeamId);

    const studentsRaw     = localStorage.getItem(expectedStudentsKey);
    const usersRaw        = localStorage.getItem(expectedUsersKey);
    const goalsRaw        = localStorage.getItem(expectedGoalsKey);
    const measurementsRaw = localStorage.getItem(expectedMeasurementsKey);
    const observationsRaw = localStorage.getItem(expectedObservationsKey);

    console.log('[StateInspector] 🔍 Expected key check:');
    console.table([
      {
        state: 'students',
        expectedKey: expectedStudentsKey,
        existsInStorage: studentsRaw !== null,
        hydratedFromCache: this.students.hydratedFromCache(),
        signalActiveTeamId: studentsTeamId,
      },
      {
        state: 'users',
        expectedKey: expectedUsersKey,
        existsInStorage: usersRaw !== null,
        hydratedFromCache: this.users.hydratedFromCache(),
        signalActiveTeamId: usersTeamId,
      },
      {
        state: 'goals',
        expectedKey: expectedGoalsKey,
        existsInStorage: goalsRaw !== null,
        hydratedFromCache: this.adminGoals.hydratedFromCache(),
        signalActiveTeamId: goalsTeamId,
      },
      {
        state: 'measurements',
        expectedKey: expectedMeasurementsKey,
        existsInStorage: measurementsRaw !== null,
        hydratedFromCache: this.adminMeasurements.hydratedFromCache(),
        signalActiveTeamId: measurementsTeamId,
      },
      {
        state: 'observations',
        expectedKey: expectedObservationsKey,
        existsInStorage: observationsRaw !== null,
        hydratedFromCache: this.adminObservations.hydratedFromCache(),
        signalActiveTeamId: observationsTeamId,
      },
    ]);

    // Also log the active team from AccountState for comparison
    console.log('[StateInspector] 🏷️ AccountState active team context:', {
      activeTeamId: this.account.activeTeam()?.id ?? 'null',
      blume_active_team_id_in_storage: localStorage.getItem('blume_active_team_id'),
    });
  }
}
